import React, { useId } from 'react';
import { Crosshair, Layers, LocateFixed, Minus, Plus } from 'lucide-react';
import type { JoystickSession, RoutePoint } from '../../data/joystick-mock';

export type MapStyleName = 'map' | 'satellite' | 'terrain';

const VIEW_W = 1000;
const VIEW_H = 620;

const ROADS = [
  'M 0 110 C 180 90, 320 140, 520 100 S 820 80, 1000 120',
  'M 0 250 C 160 230, 340 280, 560 240 S 840 220, 1000 260',
  'M 0 400 C 200 370, 380 430, 600 390 S 860 360, 1000 410',
  'M 0 530 C 220 500, 420 560, 680 510 S 900 490, 1000 540',
  'M 160 0 C 180 140, 140 260, 190 400 S 150 540, 180 620',
  'M 420 0 C 390 120, 460 240, 410 380 S 450 520, 400 620',
  'M 680 0 C 710 150, 640 280, 700 420 S 660 540, 710 620',
  'M 40 560 C 220 470, 380 420, 560 300 S 780 160, 960 70',
];

const LANES = [
  'M 70 40 C 120 160, 90 240, 150 340',
  'M 300 20 C 280 140, 340 200, 300 320 S 360 480, 280 600',
  'M 540 30 C 600 150, 520 250, 590 390 S 540 520, 610 610',
  'M 860 40 C 820 180, 900 260, 840 400 S 910 520, 860 610',
  'M 20 180 H 980',
  'M 20 330 H 980',
];

const BLOCKS: Array<[number, number, number, number]> = [
  [40, 36, 90, 48],
  [210, 28, 120, 52],
  [470, 40, 100, 44],
  [760, 34, 110, 50],
  [50, 150, 80, 46],
  [250, 160, 130, 50],
  [500, 148, 110, 56],
  [790, 156, 90, 42],
  [70, 290, 100, 48],
  [280, 300, 90, 44],
  [520, 286, 120, 52],
  [760, 300, 100, 46],
  [40, 440, 110, 42],
  [240, 450, 100, 40],
  [480, 436, 90, 48],
  [740, 448, 120, 44],
];

export function pointAlong(points: Array<{ x: number; y: number }>, progress: number): { x: number; y: number } {
  if (points.length === 0) return { x: 0, y: 0 };
  if (points.length === 1 || progress <= 0) return points[0];
  if (progress >= 1) return points[points.length - 1];
  const lengths: number[] = [];
  let total = 0;
  for (let index = 1; index < points.length; index += 1) {
    const length = Math.hypot(points[index].x - points[index - 1].x, points[index].y - points[index - 1].y);
    lengths.push(length);
    total += length;
  }
  let left = progress * total;
  for (let index = 0; index < lengths.length; index += 1) {
    if (left <= lengths[index] || index === lengths.length - 1) {
      const span = lengths[index] || 1;
      const blend = Math.min(1, left / span);
      return {
        x: points[index].x + (points[index + 1].x - points[index].x) * blend,
        y: points[index].y + (points[index + 1].y - points[index].y) * blend,
      };
    }
    left -= lengths[index];
  }
  return points[points.length - 1];
}

function pathOf(points: Array<{ x: number; y: number }>, project: (x: number, y: number) => { x: number; y: number }): string {
  return points
    .map((point, index) => {
      const placed = project(point.x, point.y);
      return `${index === 0 ? 'M' : 'L'} ${placed.x.toFixed(1)} ${placed.y.toFixed(1)}`;
    })
    .join(' ');
}

function traveledPoints(points: RoutePoint[], progress: number): Array<{ x: number; y: number }> {
  if (points.length === 0) return [];
  if (progress <= 0) return [points[0]];
  if (progress >= 1) return points;
  const head = pointAlong(points, progress);
  const lengths: number[] = [];
  let total = 0;
  for (let index = 1; index < points.length; index += 1) {
    const length = Math.hypot(points[index].x - points[index - 1].x, points[index].y - points[index - 1].y);
    lengths.push(length);
    total += length;
  }
  let left = progress * total;
  const kept: Array<{ x: number; y: number }> = [points[0]];
  for (let index = 0; index < lengths.length; index += 1) {
    if (left <= lengths[index]) {
      kept.push(head);
      return kept;
    }
    kept.push(points[index + 1]);
    left -= lengths[index];
  }
  kept.push(head);
  return kept;
}

interface JoystickMapProps {
  session: JoystickSession;
  progress: number;
  zoom: number;
  styleName: MapStyleName;
  activePointId: string | null;
  onZoom: (zoom: number) => void;
  onStyle: (styleName: MapStyleName) => void;
  onSelectPoint: (id: string) => void;
  onRecenter: () => void;
}

const STYLES: MapStyleName[] = ['map', 'satellite', 'terrain'];

const JoystickMap: React.FC<JoystickMapProps> = ({
  session,
  progress,
  zoom,
  styleName,
  activePointId,
  onZoom,
  onStyle,
  onSelectPoint,
  onRecenter,
}) => {
  const uid = useId().replace(/:/g, '');
  const project = (x: number, y: number) => ({ x: x * 10, y: y * 6.2 });
  const frame = `translate(${VIEW_W / 2} ${VIEW_H / 2}) scale(${zoom}) translate(${-VIEW_W / 2} ${-VIEW_H / 2})`;
  const route = pathOf(session.points, project);
  const done = pathOf(traveledPoints(session.points, progress), project);
  const head = project(pointAlong(session.points, progress).x, pointAlong(session.points, progress).y);
  let viaCount = 0;

  const cycleStyle = () => {
    const index = STYLES.indexOf(styleName);
    onStyle(STYLES[(index + 1) % STYLES.length]);
  };

  return (
    <div className={`js-map js-map--${styleName}`} data-testid="joystick-map">
      <p className="visually-hidden">
        {`Illustrated map for ${session.name} with ${session.points.length} route points.`}
      </p>
      <svg className="js-map__svg" viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} role="img" aria-label={`${session.name} route`}>
        <defs>
          <linearGradient id={`${uid}-route`} x1="0" y1="1" x2="1" y2="0">
            <stop offset="0" stopColor="var(--ml-primary, #7c5cff)" />
            <stop offset="1" stopColor="var(--ml-primary-soft, #b07cff)" />
          </linearGradient>
          <filter id={`${uid}-shadow`} x="-40%" y="-40%" width="180%" height="180%">
            <feDropShadow dx="0" dy="1" stdDeviation="1.6" floodOpacity="0.35" />
          </filter>
        </defs>
        <g transform={frame}>
          <rect className="js-map__paper" width={VIEW_W} height={VIEW_H} />
          {BLOCKS.map(([x, y, width, height]) => (
            <rect key={`${x}-${y}`} className="js-map__block" x={x} y={y} width={width} height={height} rx="6" />
          ))}
          <ellipse className="js-map__park" cx="210" cy="200" rx="78" ry="48" />
          <ellipse className="js-map__park" cx="540" cy="300" rx="96" ry="58" />
          <ellipse className="js-map__park" cx="300" cy="470" rx="70" ry="40" />
          <path className="js-map__water" d="M880 0c-40 90 50 130 8 220s40 90-8 170 50 110 10 230H1000V0z" />
          <g className="js-map__roads">
            {ROADS.map((d) => <path key={d} d={d} />)}
          </g>
          <g className="js-map__lanes">
            {LANES.map((d) => <path key={d} d={d} />)}
          </g>
          <g className="js-map__labels">
            {session.labels.map((label) => {
              const at = project(label.x, label.y);
              return (
                <text key={label.text} x={at.x} y={at.y} textAnchor="middle" className={label.strong ? 'is-strong' : undefined}>
                  {label.text}
                  {label.sub ? (
                    <tspan x={at.x} dy="14" className="js-map__sub">{label.sub}</tspan>
                  ) : null}
                </text>
              );
            })}
          </g>
          {route && <path className="js-map__halo" d={route} />}
          {route && <path className="js-map__route" d={route} stroke={`url(#${uid}-route)`} />}
          {done && <path className="js-map__done" d={done} />}
          {session.points.map((point) => {
            const at = project(point.x, point.y);
            const active = point.id === activePointId;
            const number = point.kind === 'via' ? viaCount += 1 : 0;
            return (
              <g
                key={point.id}
                transform={`translate(${at.x} ${at.y})`}
                className={`js-pin${active ? ' is-active' : ''}`}
                filter={`url(#${uid}-shadow)`}
                onClick={() => onSelectPoint(point.id)}
              >
                <circle className="js-pin__hit" r="22" />
                {point.kind === 'via' ? (
                  <>
                    <circle className="js-pin__via" r={active ? 18 : 15} />
                    <text className="js-pin__num" y="5" textAnchor="middle">{number}</text>
                  </>
                ) : (
                  <>
                    <path className={`js-pin__drop js-pin__drop--${point.kind}`} d="M0-30c-10 0-18 8-18 18 0 14 18 32 18 32s18-18 18-32c0-10-8-18-18-18z" />
                    <circle className="js-pin__core" cy="-12" r="6" />
                  </>
                )}
                <title>{`${point.title}, ${point.place}`}</title>
              </g>
            );
          })}
          <g transform={`translate(${head.x} ${head.y})`}>
            <circle className="js-map__head" r="12" />
            <circle className="js-map__head-core" r="5" />
          </g>
        </g>
      </svg>

      {session.status === 'live' && <span className="js-map__live">Live</span>}

      <div className="js-map__tools">
        <button type="button" aria-label={`Map style ${styleName}. Change style`} onClick={cycleStyle}>
          <Layers size={16} aria-hidden="true" />
        </button>
        <button type="button" aria-label="Reset map view" onClick={onRecenter}>
          <Crosshair size={16} aria-hidden="true" />
        </button>
        <button
          type="button"
          aria-label="Show start point"
          onClick={() => {
            const start = session.points.find((point) => point.kind === 'start');
            if (start) onSelectPoint(start.id);
          }}
        >
          <LocateFixed size={16} aria-hidden="true" />
        </button>
        <button type="button" aria-label="Zoom in" onClick={() => onZoom(Math.min(2.2, Number((zoom + 0.2).toFixed(2))))} disabled={zoom >= 2.2}>
          <Plus size={16} aria-hidden="true" />
        </button>
        <button type="button" aria-label="Zoom out" onClick={() => onZoom(Math.max(1, Number((zoom - 0.2).toFixed(2))))} disabled={zoom <= 1}>
          <Minus size={16} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

export default JoystickMap;
